import { createContext, useContext, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { User, Organisation } from "@shared/schema";
// import { useWebSocket } from "./use-websocket"; // No longer needed here

export type AuthenticatedUser = User & { 
  organisationUsers?: { role: string; organisation: Organisation }[];
};

interface AuthContextType {
  user: AuthenticatedUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  // Check for stored authentication on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const authToken = localStorage.getItem('authToken');
    
    if (storedUser && authToken) {
      try {
        const userData: AuthenticatedUser = JSON.parse(storedUser);
        setUser(userData);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('authToken');
      }
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const handleAuthError = () => {
      logout();
    };

    window.addEventListener('auth-error', handleAuthError);

    return () => {
      window.removeEventListener('auth-error', handleAuthError);
    };
  }, []);

  useEffect(() => {
    const handleUserSettingsChanged = () => {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const userData: AuthenticatedUser = JSON.parse(storedUser);
          setUser(userData);
        } catch (error) {
          console.error('Error parsing updated user data from localStorage:', error);
        }
      }
    };

    const handleUserAuthChanged = (event: CustomEvent) => {
      console.log('User auth data changed - updating context');
      setUser(event.detail);
    };

    window.addEventListener('user-settings-changed', handleUserSettingsChanged);
    window.addEventListener('user-auth-changed', handleUserAuthChanged as EventListener);

    return () => {
      window.removeEventListener('user-settings-changed', handleUserSettingsChanged);
      window.removeEventListener('user-auth-changed', handleUserAuthChanged as EventListener);
    };
  }, []);

  const logout = async () => {
    try {
      // Call server logout endpoint to clean up push subscriptions
      await fetch('/api/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Error during server logout:', error);
      // Continue with client-side cleanup even if server call fails
    }
    
    // Clear client-side data
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
    setUser(null);
    queryClient.clear();
    window.location.href = "/login";
  };

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}